import { Injectable } from '@angular/core';

import {
  ActuationDocument,
  Administrator,
  Faculty,
  SearchFilter,
  Shareholder,
  Study,
  StudyAdministrator,
  StudyFaculty,
  StudyShareholder,
} from '../../types';
import { AVAILABLE_ACTUATION_KINDS } from '../../constants/actuation-kinds';
import { PaginatedResponse, SimpleResponse } from '../types';
import { V2StudiesService, StudyUpdateParams } from './v2-studies.service';
import { AdministratorsService } from './administrators.service';
import { FacultiesService } from './faculties.service';
import { ShareholdersService } from './shareholders.service';
import {
  backendShareholderToStudyShareholder,
  backendStudyToStudy,
  studyShareholderToBackendShareholder,
} from './utils/backend-study-to-study';

@Injectable({
  providedIn: 'root',
})
export class StudiesService {
  constructor(
    private administratorsService: AdministratorsService,
    private facultiesService: FacultiesService,
    private shareholdersService: ShareholdersService,
    private v2StudiesService: V2StudiesService,
  ) {}

  public async getStudy(studyId: number): Promise<SimpleResponse<Study>> {
    const response = await this.v2StudiesService.getStudy(studyId);

    return {
      ...response,
      data: response.data && backendStudyToStudy(response.data),
    };
  }

  public getStudyFailedRules(
    studyId: number,
  ): Promise<SimpleResponse<string[]>> {
    return this.v2StudiesService.getStudyFailedRules(studyId);
  }

  public async getStudies(
    filter: SearchFilter,
    pageUrl?: string,
  ): Promise<PaginatedResponse<Study[]>> {
    const response = await this.v2StudiesService.getStudies(filter, pageUrl);

    return { ...response, results: response.results?.map(backendStudyToStudy) };
  }

  public async updateStudy(
    studyId: number,
    params: StudyUpdateParams,
  ): Promise<SimpleResponse<Study>> {
    const response = await this.v2StudiesService.updateStudy(studyId, params);

    return {
      ...response,
      data: response.data && backendStudyToStudy(response.data),
    };
  }

  public updateStudyAdministrators(
    studyId: number,
    administrators: Administrator[],
  ): Promise<SimpleResponse<StudyAdministrator[]>> {
    const studyAdministrators = administrators.map((administrator) => ({
      ...administrator,
      study: studyId,
    }));

    return this.administratorsService.updateAdministrators(studyAdministrators);
  }

  public updateStudyFaculties(
    studyId: number,
    faculties: Faculty[],
  ): Promise<SimpleResponse<StudyFaculty[]>> {
    const studyFaculties = faculties.map((faculty) => ({
      ...faculty,
      study: studyId,
    }));

    return this.facultiesService.updateFaculties(studyFaculties);
  }

  public async updateStudyShareholders(
    studyId: number,
    shareholders: Shareholder[],
  ): Promise<SimpleResponse<StudyShareholder[]>> {
    const studyShareholders = shareholders
      .map((shareholder) => ({
        ...shareholder,
        study: studyId,
      }))
      .map(studyShareholderToBackendShareholder);
    const response =
      await this.shareholdersService.updateShareholders(studyShareholders);

    return {
      ...response,
      data: response.data?.map(backendShareholderToStudyShareholder),
    };
  }

  public async updateStudyRelations(
    studyId: number,
    associates?: StudyShareholder[],
    administrators?: StudyAdministrator[],
    faculties?: StudyFaculty[],
  ): Promise<Error[]> {
    /**
     * Due to backend race conditions, we need to update the study relations
     * in a specific order to avoid conflicts.
     * This should be repsolved once the groups flow is fully implemented.
     */
    const errors = [];

    if (associates) {
      const { error } = await this.updateStudyShareholders(studyId, associates);
      errors.push(error);
    }
    if (administrators) {
      const { error } = await this.updateStudyAdministrators(
        studyId,
        administrators,
      );
      errors.push(error);
    }
    if (faculties) {
      const { error } = await this.updateStudyFaculties(studyId, faculties);
      errors.push(error);
    }

    return errors.filter(Boolean);
  }

  public async getStudyActuationByCve(
    cve: string,
  ): Promise<SimpleResponse<ActuationDocument>> {
    const response = await this.v2StudiesService.getStudyActuationByCve(cve);

    return {
      ...response,
      data: response.data,
    };
  }

  async getStudyWithHistory(
    studyId: number,
  ): Promise<SimpleResponse<{ study: Study; documents: ActuationDocument[] }>> {
    const { data: study, error: studyError } = await this.getStudy(studyId);

    if (studyError) {
      return { data: null, error: studyError };
    }

    const actuationsMetadata = study.actuations
      .filter((actuation) => AVAILABLE_ACTUATION_KINDS.includes(actuation.kind))
      .sort(
        (a, b) =>
          new Date(a.documentDate).getTime() -
          new Date(b.documentDate).getTime(),
      );

    const responses = await Promise.all(
      actuationsMetadata.map((actuation) =>
        this.getStudyActuationByCve(actuation.cve),
      ),
    );

    const historyError = responses.find((response) => response.error);

    if (historyError) {
      return {
        data: null,
        error: historyError.error,
      };
    }

    const documents = responses
      .map((response) => response.data)
      .filter(Boolean);

    return {
      data: {
        study,
        documents,
      },
      error: null,
    };
  }
}
